import { doneBy, undoVariant } from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useLive } from './live';

/** How long a change told stays on the screen. */
const SHOWN_MS = 10_000;

/** The page a change is seen on, by what it changed. */
function pageOf(code: string): string {
  const what = code.slice(0, code.indexOf('.'));
  const pages: Record<string, string> = {
    bookings: '/bookings',
    costs: '/group',
    people: '/sharing',
    spends: '/today',
    rates: '/today',
    documents: '/docs',
    before: '/docs',
    leave: '/calendar',
    mail: '/bookings/mail',
    flights: '/flights',
    areas: '/settings',
  };
  return pages[what] ?? '';
}

/** One change told: who did what, when, and where to see it; gone after a while or when closed. */
function Told({
  trip,
  entry,
}: {
  trip: string;
  entry: ReturnType<typeof useLive>['told'][number];
}) {
  const { t } = useTranslation();
  const { dismiss, refresh } = useLive();
  const [undoing, setUndoing] = useState<'no' | 'busy' | 'failed'>('no');
  const onGone = () => {
    dismiss(entry.id);
  };
  useEffect(() => {
    const timer = window.setTimeout(() => {
      dismiss(entry.id);
    }, SHOWN_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [dismiss, entry.id]);
  const name = doneBy(entry, t('activity.someone'));
  // A plan changed by one's assistant is taken back here at once.
  const undo = () => {
    setUndoing('busy');
    undoVariant(trip).then(
      () => {
        refresh();
        onGone();
      },
      () => {
        setUndoing('failed');
      },
    );
  };
  return (
    <li className="toast">
      <span className="toast-mark" aria-hidden="true">
        {name.slice(0, 1).toUpperCase()}
      </span>
      <span>
        {t(`activity.code.${entry.code.replace('.', '_')}`, {
          name,
          about: entry.about,
          defaultValue: t('activity.changed', { name }),
        })}
        <small>{new Date(entry.at).toTimeString().slice(0, 5)}</small>
        {undoing === 'failed' && <small role="alert">{t('live.undoFailed')}</small>}
      </span>
      {entry.via !== null && entry.code === 'route.change' && (
        <button
          type="button"
          className="link-button"
          disabled={undoing === 'busy'}
          onClick={undo}
        >
          {t('live.undo')}
        </button>
      )}
      <Link className="link-button" to={`/trips/${trip}${pageOf(entry.code)}`} onClick={onGone}>
        {t('live.see')}
      </Link>
      <button
        type="button"
        className="icon-button icon-button-small"
        aria-label={t('live.close')}
        onClick={onGone}
      >
        ×
      </button>
    </li>
  );
}

/** What others changed on the trip open, said as it happens (the `Live` mockup's toasts). */
export function LiveToasts({ trip }: { trip: string }) {
  const { told } = useLive();
  if (told.length === 0) return null;
  return (
    <ul className="toasts" aria-live="polite">
      {told.map((entry) => (
        <Told key={entry.id} trip={trip} entry={entry} />
      ))}
    </ul>
  );
}
